import { lazy, type ReactNode, Suspense, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { ApiError, ForbiddenError } from "@/api/errors";
import { processDesignModelPath, ROUTES } from "@/app/routes";
import { CollapseChevron } from "@/components/layout/collapsible-section";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { validateNewFormFileName } from "@/domain/process-model/form-files";
import {
  type EmptyGroup,
  findEmptyGroups,
  groupModelsByPath,
  type ModelGroup,
  searchModels,
} from "@/domain/process-model/group-models";

import { buildFormTemplate } from "./build-form-template";
import { downloadBpmn } from "./download-bpmn";
import { FormFileEditor } from "./form-file-editor";
import { ImportDiagramPanel } from "./import-diagram-panel";
import { ImportFormFilesPanel } from "./import-form-files-panel";
import { InlineConfirm } from "./inline-confirm";
import { MetadataPathsSection } from "./metadata-paths-section";
import { ModelReadme } from "./model-readme";
import { NewModelForm } from "./new-model-form";
import { useCreateModelFile } from "./use-create-model-file";
import {
  GroupNotEmptyError,
  useDeleteGroup,
  useDeleteModel,
} from "./use-delete-model";
import type { DesignModel } from "./use-design-models";
import { useDesignModels } from "./use-design-models";
import { useMessageOwner } from "./use-message-owner";
import { useProcessGroups } from "./use-process-groups";
import {
  type ModelFile,
  useProcessModel,
  useProcessModelFile,
} from "./use-process-model";
import { useSaveModelFile } from "./use-save-model-file";

/** 図は開いたときだけ読み込む（bpmn-js は重い）。 */
const BpmnViewer = lazy(() =>
  import("@/features/case-diagram/bpmn-viewer").then((m) => ({
    default: m.BpmnViewer,
  })),
);

/** 編集は開いたときだけ読み込む（Modeler は Viewer よりさらに重い）。 */
const BpmnEditor = lazy(() =>
  import("@/features/case-diagram/bpmn-editor").then((m) => ({
    default: m.BpmnEditor,
  })),
);

const NO_MARKS = { current: [], completed: [], error: [] } as const;

/**
 * 大項目8「プロセス設計」（モデラー）。
 *
 * 図・説明（README.md）・フォーム定義を 1 画面で扱う。フォームを別画面にしていた
 * ときは同じモデルを 2 回選び直す必要があり、図との対応も追いにくかった。
 * 選んだモデルは URL に載せる（再読込とリンク共有のため）。
 *
 * 図はこの画面で編集し、保存は backend の process_model_file_update
 * （multipart PUT）でサーバ上のモデルを更新する。読み込み時のハッシュを添えて
 * 他の人の変更を黙って上書きしない（409 は書き出しを促す）。
 *
 * モデルの作成（雛形 / 手持ちの .bpmn）・削除・空グループの削除・図の取り込み
 * （主 BPMN の差し替え）もここで行う。取り込みはハッシュを付けない（置き換えを
 * 確認欄で承諾する操作のため。use-replace-diagram）。説明（.md）とフォーム定義
 * （.json）も手元のファイルから入れられる（model-readme / import-form-files-panel）。
 *
 * ただし backend が書くのはコンテナ内の process-models で、リポジトリの
 * process-models/ とは別。作成・削除・取り込みも同じで、デプロイ（deploy-models.sh）で
 * 上書きされるため、残したい変更は書き出すか pull-models.sh で引き戻してコミットする。
 */
export function ProcessDesignPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const params = useParams();
  const models = useDesignModels();
  const processGroups = useProcessGroups();
  /** 新規作成中は右ペインにフォームを出す（URL には載せない）。 */
  const [isCreating, setIsCreating] = useState(false);
  /** 削除の確認欄を開いている空グループ。 */
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  /** 利用者が明示的に開閉したグループ（既定と違えたいものだけ持つ）。 */
  const [toggled, setToggled] = useState<Readonly<Record<string, boolean>>>({});

  // 選択は URL に載せる（リロード・共有で保つ）
  const selectedId = (params.modifiedModelId ?? "").replace(/:/g, "/");

  // 階層はモデル id のパスから導く（グループの表示名は API の parent_groups 由来）
  const groups = groupModelsByPath(searchModels(models.data ?? [], keyword));
  const isSearching = keyword.trim() !== "";
  // モデルから組み立てる階層には空のグループが出ないので、グループ一覧と突き合わせる。
  // 両方の一覧が揃うまでは出さない。モデル一覧が読み込み中・取得失敗のときに
  // 突き合わせると全グループが「0 件」に見え、配下ごと消える削除を出してしまう。
  // 検索中も出さない（検索に当たったモデルだけを見せる）
  const emptyGroups =
    isSearching || !models.isSuccess || !processGroups.isSuccess
      ? []
      : findEmptyGroups(
          processGroups.data,
          models.data.map((model) => model.processModelId),
        );
  const navEntries = [
    ...groups.map((group) => ({ path: group.path, models: group })),
    ...emptyGroups.map((group) => ({ path: group.id, empty: group })),
  ].sort((a, b) => a.path.localeCompare(b.path, "ja"));

  const openModel = (processModelId: string) => {
    setIsCreating(false);
    void navigate(processDesignModelPath(processModelId));
  };

  /**
   * グループを開くか。
   *
   * 既定は畳む。ただし「見ているモデルのグループ」と「グループが 1 つだけ」は
   * 畳んでも選ぶ余地がないので開く。検索中は開く（絞った結果が畳まれて見えないと
   * 検索した意味がない）ので、開閉の指定より優先する。
   */
  const isGroupOpen = (group: ModelGroup<DesignModel>): boolean => {
    if (isSearching) return true;
    return (
      toggled[group.path] ??
      (groups.length === 1 ||
        group.models.some((model) => model.processModelId === selectedId))
    );
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t("processDesign.title")}</h1>
        <p className="text-muted-foreground text-sm">
          {t("processDesign.description")}
        </p>
      </header>

      <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
        {t("processDesign.editNote")}
      </p>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <nav className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">
              {t("processDesign.models")}
            </h2>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setIsCreating(true)}
            >
              {t("processDesign.newModel.open")}
            </Button>
          </div>

          <SearchBox
            label={t("processDesign.searchModels")}
            value={keyword}
            onChange={setKeyword}
          />

          {models.isPending ? <Skeleton className="h-24 w-full" /> : null}
          {models.data?.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("processDesign.empty")}
            </p>
          ) : null}
          {models.data !== undefined &&
          models.data.length > 0 &&
          groups.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("processDesign.noMatch")}
            </p>
          ) : null}
          {navEntries.map((entry) =>
            "empty" in entry ? (
              <EmptyGroupNav
                key={entry.path}
                group={entry.empty}
                isConfirming={deletingGroup === entry.path}
                onAsk={() =>
                  setDeletingGroup((current) =>
                    current === entry.path ? null : entry.path,
                  )
                }
                onClose={() => setDeletingGroup(null)}
              />
            ) : (
              <ModelGroupNav
                key={entry.path}
                group={entry.models}
                selectedId={isCreating ? "" : selectedId}
                isOpen={isGroupOpen(entry.models)}
                onToggle={(open) =>
                  setToggled((current) => ({
                    ...current,
                    [entry.path]: open,
                  }))
                }
                // 作成中に一覧のモデルを選んだら、フォームを閉じてそのモデルを開く
                onSelect={() => setIsCreating(false)}
              />
            ),
          )}
        </nav>

        {isCreating ? (
          <NewModelForm
            onCancel={() => setIsCreating(false)}
            // 作ったモデルをそのまま編集できるように開く
            onCreated={openModel}
            onOpenModel={openModel}
          />
        ) : selectedId === "" ? (
          <p className="text-muted-foreground text-sm">
            {t("processDesign.selectModel")}
          </p>
        ) : (
          // モデルを切り替えたら確認欄・編集モードを持ち越さない
          <ModelDetail key={selectedId} processModelId={selectedId} />
        )}
      </div>
    </section>
  );
}

/**
 * 左の一覧のグループ 1 つ。
 *
 * 開閉は URL（見ているモデル）から既定が決まるので、状態は親が持つ
 * （components/layout/collapsible-section.tsx の自前状態では URL に追従できない）。
 * グループ名の無いモデル（グループ直下）は畳む対象にしない。
 */
function ModelGroupNav({
  group,
  selectedId,
  isOpen,
  onToggle,
  onSelect,
}: {
  readonly group: ModelGroup<DesignModel>;
  readonly selectedId: string;
  readonly isOpen: boolean;
  readonly onToggle: (_open: boolean) => void;
  readonly onSelect: () => void;
}) {
  const models = (
    <ul className="flex flex-col gap-1">
      {group.models.map((model) => (
        <li key={model.processModelId}>
          <Link
            to={processDesignModelPath(model.processModelId)}
            onClick={onSelect}
            aria-current={
              selectedId === model.processModelId ? "page" : undefined
            }
            className={`block w-full rounded-md border px-3 py-2 text-left text-sm ${
              selectedId === model.processModelId
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary"
            }`}
          >
            <span className="block font-medium">{model.displayName}</span>
            <span className="text-muted-foreground block text-xs">
              {model.processModelId}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );

  if (group.label === "")
    return <div className="flex flex-col gap-1">{models}</div>;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => onToggle(!isOpen)}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 px-1 text-xs font-semibold"
      >
        <CollapseChevron open={isOpen} />
        <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
        <span className="tabular-nums">{group.models.length}</span>
      </button>
      {isOpen ? models : null}
    </div>
  );
}

/**
 * 配下にモデルが無いグループ。件数 0 と削除の導線を出す。
 *
 * 削除はこの行にだけ置く。backend のグループ削除は配下ごと消す（rmtree）ので、
 * モデルのあるグループからは消させない。
 */
function EmptyGroupNav({
  group,
  isConfirming,
  onAsk,
  onClose,
}: {
  readonly group: EmptyGroup;
  readonly isConfirming: boolean;
  readonly onAsk: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const remove = useDeleteGroup();
  const label = t("processDesign.remove.groupLabel", { name: group.label });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 px-1">
        <span className="text-muted-foreground flex min-w-0 flex-1 items-center gap-1 text-xs font-semibold">
          {/* 開閉できないので矢印（CollapseChevron）と同じ幅を空ける（見出しの位置をそろえる） */}
          <span aria-hidden="true" className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{group.label}</span>
          <span className="tabular-nums">0</span>
        </span>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-label={label}
          aria-expanded={isConfirming}
          onClick={() => {
            remove.reset();
            onAsk();
          }}
        >
          {t("processDesign.remove.group")}
        </Button>
      </div>
      {isConfirming ? (
        <InlineConfirm
          label={label}
          destructive
          confirmLabel={t("processDesign.remove.submit")}
          cancelLabel={t("processDesign.remove.cancel")}
          isPending={remove.isPending}
          error={remove.isError ? removeErrorMessage(remove.error, t) : null}
          onCancel={onClose}
          onConfirm={() => {
            // 消すと一覧が読み直されてこの行が先に消えることがある。mutate の
            // コールバックは消えた部品では呼ばれないので、Promise で閉じる
            void remove
              .mutateAsync({ processGroupId: group.id, label: group.label })
              .then(onClose, () => {
                // 失敗はこの確認欄の中に出す（remove.error）
              });
          }}
        >
          <p className="m-0">
            {t("processDesign.remove.confirmGroup", {
              name: group.label,
              id: group.id,
            })}
          </p>
        </InlineConfirm>
      ) : null}
    </div>
  );
}

/**
 * 削除の失敗理由。案件がある（400 existing_instances）・権限なし・グループに
 * モデルがあった（消す直前の確認で分かった）を出し分ける。
 */
function removeErrorMessage(
  error: Error,
  t: ReturnType<typeof useTranslation<"common">>["t"],
): string {
  if (error instanceof GroupNotEmptyError) {
    return t("processDesign.remove.notEmpty");
  }
  if (error instanceof ForbiddenError) {
    return t("processDesign.remove.forbidden");
  }
  if (error instanceof ApiError && error.errorCode === "existing_instances") {
    return t("processDesign.remove.hasInstances");
  }
  return t("processDesign.remove.failed");
}

function ModelDetail({ processModelId }: { readonly processModelId: string }) {
  const { t } = useTranslation("common");
  const { data, isPending, isError } = useProcessModel(processModelId);
  const diagramFile = data?.primaryFileName ?? null;
  const diagram = useProcessModelFile(processModelId, diagramFile);
  const [isEditing, setIsEditing] = useState(false);
  /** 「取り込み」の確認欄。閲覧中だけ開ける（未保存の編集を上書きしない）。 */
  const [isImporting, setIsImporting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const remove = useDeleteModel();
  const navigate = useNavigate();
  /** フォーム定義のうち開いているファイル（同時に 1 つ）。 */
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [isAddingForm, setIsAddingForm] = useState(false);
  /** 「ファイルを取り込む」の確認欄（手元の *-schema.json / *-uischema.json を置く）。 */
  const [isImportingForms, setIsImportingForms] = useState(false);
  /** 取り込みの送信中。確認欄を閉じると途中の失敗理由を出す場所が無くなるので、閉じる導線を止める。 */
  const [isImportPending, setIsImportPending] = useState(false);
  /** 図で編集中のメッセージ名（衝突の照会に使う）。 */
  const [messageName, setMessageName] = useState<string | null>(null);
  const messageOwner = useMessageOwner(messageName);
  const save = useSaveModelFile();
  const contents = diagram.data?.contents ?? "";
  const contentsHash = diagram.data?.contentsHash ?? null;

  if (isPending) return <Skeleton className="h-72 w-full" />;
  if (isError || !data) {
    return <p className="text-destructive text-sm">{t("state.error")}</p>;
  }

  const saveDiagram = (xml: string) => {
    save.mutate({
      processModelId,
      fileName: diagramFile ?? "",
      contents: xml,
      contentsHash,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold">{data.displayName}</h2>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-expanded={isConfirmingDelete}
            onClick={() => {
              remove.reset();
              setIsConfirmingDelete((open) => !open);
            }}
          >
            {t("processDesign.remove.model")}
          </Button>
        </div>
        {data.description === null ? null : (
          <p className="text-muted-foreground text-sm">{data.description}</p>
        )}
        {data.primaryProcessId === null ? null : (
          <p className="text-muted-foreground text-xs">
            {t("processDesign.primaryProcess")}:{" "}
            <span className="font-mono">{data.primaryProcessId}</span>
          </p>
        )}
      </div>

      {isConfirmingDelete ? (
        <InlineConfirm
          label={t("processDesign.remove.model")}
          destructive
          confirmLabel={t("processDesign.remove.submit")}
          cancelLabel={t("processDesign.remove.cancel")}
          isPending={remove.isPending}
          error={remove.isError ? removeErrorMessage(remove.error, t) : null}
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={() => {
            void remove
              .mutateAsync({ processModelId, displayName: data.displayName })
              // 消えたモデルの URL に居続けないよう、未選択の一覧に戻す
              .then(
                () => navigate(ROUTES.processDesign),
                () => {
                  // 失敗はこの確認欄の中に出す（remove.error）
                },
              );
          }}
        >
          <p className="m-0">
            {t("processDesign.remove.confirmModel", {
              name: data.displayName,
              id: processModelId,
            })}
          </p>
        </InlineConfirm>
      ) : null}

      {/* 図を先に出す（直感的に把握できる）。説明はその下で読む */}
      {diagram.data && contents !== "" ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">
            {t("processDesign.diagramTitle")}
          </h3>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-xs">
              {isEditing ? t("processDesign.exportHint") : null}
            </span>
            <div className="flex items-center gap-2">
              {/* 書き出しは両方のモードに出す。編集中は競合したときの逃げ道にもなる
                  （backend の 409 メッセージも書き出しを促す） */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  downloadBpmn(diagramFile ?? "process.bpmn", contents)
                }
              >
                {t("processDesign.download")}
              </Button>
              {/* 取り込み（書き出しの逆）は隣に置く。編集中は未保存の編集を
                  上書きしてしまうので出さない */}
              {isEditing ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-expanded={isImporting}
                  onClick={() => setIsImporting((open) => !open)}
                >
                  {t("processDesign.importDiagram.open")}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsImporting(false);
                  setIsEditing((editing) => !editing);
                }}
              >
                {t(
                  isEditing ? "processDesign.backToView" : "processDesign.edit",
                )}
              </Button>
            </div>
          </div>

          <SaveState mutation={save} />
          {isImporting && !isEditing && diagramFile !== null ? (
            <ImportDiagramPanel
              processModelId={processModelId}
              primaryFileName={diagramFile}
              currentProcessId={data.primaryProcessId}
              onClose={() => setIsImporting(false)}
            />
          ) : null}

          <Suspense fallback={<Skeleton className="h-72 w-full" />}>
            {isEditing ? (
              <BpmnEditor
                xml={contents}
                onSave={saveDiagram}
                isSaving={save.isPending}
                // User Task にフォームを紐付けられるよう候補を渡す
                formFiles={data.forms.map((file) => file.name)}
                // メッセージ名が他のモデルと衝突していれば保存前に知らせる
                messageNameOwner={
                  messageOwner.data !== undefined &&
                  messageOwner.data !== null &&
                  messageOwner.data !== processModelId
                    ? messageOwner.data
                    : null
                }
                onMessageNameChange={setMessageName}
              />
            ) : (
              <BpmnViewer xml={contents} marks={NO_MARKS} />
            )}
          </Suspense>
        </div>
      ) : diagram.isPending && diagramFile !== null ? (
        <Skeleton className="h-72 w-full" />
      ) : null}

      <ModelReadme processModelId={processModelId} />

      <MetadataPathsSection
        processModelId={processModelId}
        formFiles={data.forms.map((file) => file.name)}
        current={data.metadataPaths}
      />

      <FileList label={t("processDesign.diagrams")} files={data.diagrams} />

      {/* フォーム定義は名前を押すと中を開いて編集できる（旧「フォーム設計」画面） */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t("processDesign.forms")}</h3>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isImportPending}
              onClick={() => {
                setIsImportingForms(false);
                setIsAddingForm(true);
              }}
            >
              {t("processDesign.newForm.open")}
            </Button>
            {/* 手元のファイルをそのまま置く（名前を手入力しないので、BPMN が指す
                名前とずれない）。雛形から作る「フォームを追加」とは別の口 */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-expanded={isImportingForms}
              disabled={isImportPending}
              onClick={() => {
                setIsAddingForm(false);
                setIsImportingForms((open) => !open);
              }}
            >
              {t("processDesign.importForms.open")}
            </Button>
          </div>
        </div>

        {isAddingForm ? (
          <NewFormFileForm
            processModelId={processModelId}
            existing={data.forms.map((file) => file.name)}
            onCancel={() => setIsAddingForm(false)}
            onCreated={(fileName) => {
              setIsAddingForm(false);
              // 作ったファイルをそのまま編集できるように開く
              setOpenForm(fileName);
            }}
          />
        ) : null}

        {isImportingForms ? (
          <ImportFormFilesPanel
            processModelId={processModelId}
            existing={data.forms.map((file) => file.name)}
            onClose={() => setIsImportingForms(false)}
            onPendingChange={setIsImportPending}
          />
        ) : null}

        {data.forms.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("processDesign.newForm.empty")}
          </p>
        ) : null}

        <FileList
          files={data.forms}
          openFile={openForm}
          onToggle={(fileName) =>
            setOpenForm((current) => (current === fileName ? null : fileName))
          }
          renderOpen={(fileName) => (
            <FormFileEditor
              processModelId={processModelId}
              fileName={fileName}
            />
          )}
        />
      </section>

      <FileList label={t("processDesign.others")} files={data.others} />
    </div>
  );
}

/** 検証で弾いた理由 → 文言のキー。 */
const NAME_ISSUE_KEYS = {
  empty: "processDesign.newForm.issueEmpty",
  invalid: "processDesign.newForm.issueInvalid",
  convention: "processDesign.newForm.issueConvention",
  duplicate: "processDesign.newForm.issueDuplicate",
} as const;

/**
 * フォーム定義の新規作成。
 *
 * ファイル名だけ聞いて雛形を置く。名前を規約（`*-schema.json` /
 * `*-uischema.json`）に縛るのは、外れた名前だと**紐付けの候補に出てこない**
 * ファイルができてしまうため（作れたのに選べない、が一番分かりにくい）。
 */
function NewFormFileForm({
  processModelId,
  existing,
  onCreated,
  onCancel,
}: {
  readonly processModelId: string;
  readonly existing: readonly string[];
  readonly onCreated: (_fileName: string) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation("common");
  const id = useId();
  const [fileName, setFileName] = useState("");
  const create = useCreateModelFile();

  const trimmed = fileName.trim();
  const check = validateNewFormFileName(fileName, existing);
  // 入力前から赤字を出さない（空は「まだ書いていない」だけ）
  const issue = check.ok || trimmed === "" ? null : check.reason;

  return (
    <form
      className="border-border flex flex-col gap-2 rounded-md border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!check.ok || create.isPending) return;
        create.mutate(
          {
            processModelId,
            fileName: trimmed,
            contents: buildFormTemplate(trimmed),
          },
          { onSuccess: () => onCreated(trimmed) },
        );
      }}
    >
      <label htmlFor={id} className="text-sm font-medium">
        {t("processDesign.newForm.fileName")}
      </label>
      <input
        id={id}
        value={fileName}
        onChange={(event) => setFileName(event.target.value)}
        placeholder="review-schema.json"
        className="border-input bg-background rounded-md border px-2 py-1.5 text-sm"
      />
      <p className="text-muted-foreground text-xs">
        {t("processDesign.newForm.fileNameHint")}
      </p>

      {issue === null ? null : (
        <p role="alert" className="text-destructive text-xs">
          {t(NAME_ISSUE_KEYS[issue])}
        </p>
      )}
      {create.isError ? (
        <p role="alert" className="text-destructive text-xs">
          {t("processDesign.newForm.failed")}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!check.ok || create.isPending}
        >
          {t("processDesign.newModel.submit")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          {t("processDesign.newModel.cancel")}
        </Button>
      </div>
    </form>
  );
}

/** 保存の結果。競合（409）は原因が違うので別の文言にする。 */
function SaveState({
  mutation,
}: {
  readonly mutation: ReturnType<typeof useSaveModelFile>;
}) {
  const { t } = useTranslation("common");

  if (mutation.isSuccess) {
    return <p className="text-sm font-medium">{t("processDesign.saved")}</p>;
  }
  if (!mutation.isError) return null;

  const isConflict =
    mutation.error instanceof ApiError && mutation.error.status === 409;

  return (
    <p role="alert" className="text-destructive text-sm">
      {t(
        isConflict ? "processDesign.saveConflict" : "processDesign.saveFailed",
      )}
    </p>
  );
}

function FileList({
  label,
  files,
  openFile,
  onToggle,
  renderOpen,
}: {
  /** 見出し。呼び出し側が自分で出す場合は省略する。 */
  readonly label?: string;
  readonly files: readonly ModelFile[];
  /** 開いているファイル名（開閉できる一覧のときだけ渡す）。 */
  readonly openFile?: string | null;
  readonly onToggle?: (_fileName: string) => void;
  readonly renderOpen?: (_fileName: string) => ReactNode;
}) {
  if (files.length === 0) return null;

  const isInteractive = onToggle !== undefined && renderOpen !== undefined;

  return (
    <section className="flex flex-col gap-1">
      {label === undefined ? null : (
        <h3 className="text-sm font-semibold">{label}</h3>
      )}
      <ul className="flex flex-col gap-1 text-sm">
        {files.map((file) => (
          <li key={file.name} className="flex flex-col gap-2">
            {isInteractive ? (
              <button
                type="button"
                aria-expanded={openFile === file.name}
                onClick={() => onToggle(file.name)}
                className={`flex w-full items-baseline gap-2 rounded-md border px-3 py-2 text-left ${
                  openFile === file.name
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary"
                }`}
              >
                <span className="font-mono text-xs">{file.name}</span>
                <FileSize size={file.size} />
              </button>
            ) : (
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-xs">{file.name}</span>
                <FileSize size={file.size} />
              </span>
            )}

            {isInteractive && openFile === file.name
              ? renderOpen(file.name)
              : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function FileSize({ size }: { readonly size: number | null }) {
  if (size === null) return null;

  return (
    <span className="text-muted-foreground text-xs tabular-nums">
      {size.toLocaleString()} B
    </span>
  );
}

/** 一覧の絞り込み用の検索欄。 */
function SearchBox({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (_value: string) => void;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        placeholder={label}
        onChange={(event) => onChange(event.target.value)}
        className="border-input bg-background rounded-md border px-2 py-1.5 text-sm"
      />
    </div>
  );
}
